'use client'

import { useEffect, useMemo, useState } from 'react'
import { parsePortalHost, portalOrigin, type Portal } from '@/lib/subdomain'

/**
 * Which standalone portal the current host resolves to, read client-side from
 * `window.location`. Returns `"admin"` on admin.42a.ai, `"partners"` on
 * partners.42a.ai (and their *.localtest.me dev equivalents), and `null` on the
 * regular app host / partner white-label subdomains.
 *
 * Mirrors the client-side subdomain read in ReferralCodeContext / LocaleProvider
 * (window.location, not the middleware header) so a single source of truth drives
 * the portal experience. Returns `null` during SSR; portal routing/gating runs in
 * client components (ConditionalLayout, auth pages) so first-paint null is fine.
 */
export default function usePortal(): Portal | null {
  return useMemo(
    () => (typeof window !== 'undefined' ? parsePortalHost(window.location.host) : null),
    [],
  )
}

/** The path a signed-in user should land on for a given portal host. */
export function portalHomePath(portal: Portal | null): string {
  if (portal === 'admin') return '/admin'
  if (portal === 'partners') return '/partners'
  return '/dashboard'
}

/**
 * Absolute href to a portal (e.g. the "Admin console" / "Partner portal" links
 * in the user menu, opened in a new tab). Resolves the portal subdomain from
 * the current host via `portalOrigin` — `admin.42a.ai` in prod,
 * `admin.localtest.me:<port>` in local dev.
 *
 * SSR-safe: renders the same-origin `path` on the server and first client paint,
 * then upgrades to the absolute cross-subdomain URL after mount (the host is
 * only known client-side), so there is no hydration attribute mismatch. Falls
 * back to `path` on hosts with no portal subdomain (Vercel previews, custom
 * domains) — a same-origin navigation still reaches the portal there.
 */
export function usePortalHref(portal: Portal, path: string): string {
  const [loc, setLoc] = useState<{ host: string; protocol: string } | null>(null)

  useEffect(() => {
    setLoc({ host: window.location.host, protocol: window.location.protocol })
  }, [])

  return useMemo(() => {
    if (!loc) return path
    const origin = portalOrigin(portal, loc.host, loc.protocol)
    return origin ? origin + path : path
  }, [loc, portal, path])
}
